/**
 * ImagePool — cycle-through-images widget (inline ‹/› arrows + counter),
 * used both to let a node pick from its ingredient's/transformation's pool
 * of images (mode: 'pick') and to let an ingredient/transformation manage
 * its own pool — set the default, delete one, add one (mode: 'manage').
 *
 * Three independent view states: showing one of the real pool images,
 * showing "no image" (pick mode only, reached via the × button), or
 * showing the add-new panel (reached via the + button). The ‹/› arrows
 * only ever move between real pool images — never through "no image" or
 * "add new", which are dedicated buttons instead.
 *
 * Reuses ImageCropper (via an unmanaged ImageUploader instance) for the
 * "add new" step rather than reimplementing crop/upload UI; the actual
 * upload call is supplied by the caller (`uploadFn`) since the destination
 * endpoint differs by context (an ingredient's/transformation's pool, or a
 * node's own unpooled image).
 */
import { ImageUploader } from './ImageUploader.js';

export interface ImagePoolItem {
  id: string;
  url: string;
  isDefault?: boolean;
}

export interface ImagePoolOptions {
  container: HTMLElement;
  idPrefix?: string;
  images: ImagePoolItem[];
  mode: 'pick' | 'manage';

  /** pick mode only: the node's current image url, to position the cursor initially. */
  selectedUrl?: string;

  /** Upload an already-cropped blob wherever this context's images belong; returns the new item. */
  uploadFn: (blob: Blob) => Promise<ImagePoolItem>;

  /** pick mode: fired whenever the selection changes — null means "no image". */
  onSelect?: (item: ImagePoolItem | null) => void;
  /** manage mode: perform the delete call for the currently-displayed image. */
  deleteFn?: (imageId: string) => Promise<void>;
  /** manage mode: perform the set-default call for the currently-displayed image. */
  setDefaultFn?: (imageId: string) => Promise<void>;
  /** Fired after any successful add/delete/set-default, with the resulting pool. */
  onPoolChanged?: (images: ImagePoolItem[]) => void;
}

type ViewState =
  | { kind: 'image'; index: number }
  | { kind: 'none' }
  | { kind: 'add' };

export class ImagePool {
  private container: HTMLElement;
  private options: ImagePoolOptions;
  private idPrefix: string;
  private images: ImagePoolItem[];
  private view: ViewState;
  private preAddView: ViewState | null = null;
  private deleteArmed: boolean = false;
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;

  constructor(options: ImagePoolOptions) {
    this.container = options.container;
    this.options = options;
    this.idPrefix = options.idPrefix || 'image-pool';
    this.images = [...options.images];

    if (options.mode === 'pick') {
      const index = options.selectedUrl
        ? this.images.findIndex(item => item.url === options.selectedUrl)
        : -1;
      this.view = index >= 0 ? { kind: 'image', index } : { kind: 'none' };
    } else {
      this.view = this.images.length > 0 ? { kind: 'image', index: 0 } : { kind: 'none' };
    }
  }

  render(): void {
    this.container.innerHTML = '';
    // Also removes the pending outside-click listener, if a delete was
    // armed — otherwise it leaks: it's never cleaned up on the "user does
    // something else instead of confirming" path, and would keep firing
    // (an inert no-op against the now-detached button) on every future
    // click anywhere on the page for the rest of the session.
    this.disarmDelete();

    // Keep the view state consistent with the current pool contents —
    // but never override an in-progress 'add', even with an empty pool
    // (a bare node, or a freshly-created entity, has none to start with —
    // otherwise this clamp would revert straight back to 'none' the
    // instant enterAdd() sets it, and the "+" button would appear to do
    // nothing at all whenever the pool starts out empty).
    if (this.images.length === 0 && this.view.kind !== 'add') {
      this.view = { kind: 'none' };
    } else if (this.view.kind === 'image' && this.view.index >= this.images.length) {
      this.view = { kind: 'image', index: this.images.length - 1 };
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'flex flex-col items-center gap-2';

    if (this.view.kind === 'add') {
      this.renderAddView(wrapper);
    } else if (this.view.kind === 'none') {
      this.renderNoneView(wrapper);
    } else {
      this.renderImageView(wrapper, this.images[this.view.index]);
    }

    this.renderControlsRow(wrapper);

    this.container.appendChild(wrapper);
  }

  // ─── Views ──────────────────────────────────────────────────

  private renderImageView(wrapper: HTMLElement, current: ImagePoolItem): void {
    const showArrows = this.images.length > 1;

    const row = document.createElement('div');
    row.className = 'flex items-center justify-center gap-3';

    if (showArrows) row.appendChild(this.buildIconButton('‹', 'Imatge anterior', () => this.cycle(-1)));

    const previewWrap = document.createElement('div');
    previewWrap.className = 'relative w-32 h-32';

    const img = document.createElement('img');
    img.src = current.url;
    img.alt = '';
    img.className = 'w-full h-full object-cover rounded-full border-2 border-gray-200 block';
    img.onerror = () => { img.style.display = 'none'; };
    previewWrap.appendChild(img);

    if (this.options.mode === 'manage') {
      previewWrap.appendChild(this.buildStarButton(current));
    }
    const closeBtn = this.buildCloseButton(current);
    if (closeBtn) previewWrap.appendChild(closeBtn);
    row.appendChild(previewWrap);

    if (showArrows) row.appendChild(this.buildIconButton('›', 'Imatge següent', () => this.cycle(1)));
    wrapper.appendChild(row);

    if (showArrows) {
      const counter = document.createElement('div');
      counter.className = 'text-xs text-[#888] font-mono';
      counter.textContent = `${this.images.indexOf(current) + 1} / ${this.images.length}`;
      wrapper.appendChild(counter);
    }
  }

  private renderNoneView(wrapper: HTMLElement): void {
    const row = document.createElement('div');
    row.className = 'flex items-center justify-center gap-3';

    // The only way back into the pool from "no image" — cycling itself is
    // otherwise only ever reachable by browsing an actual image first.
    const showRightArrow = this.options.mode === 'pick' && this.images.length > 0;

    // A same-size invisible stand-in for the missing left arrow, so the
    // circle lands in the same spot as when both arrows are present.
    if (showRightArrow) row.appendChild(this.buildArrowSpacer());

    const placeholder = document.createElement('div');
    placeholder.className = 'w-32 h-32 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400 text-center px-2';
    placeholder.textContent = this.options.mode === 'manage' ? 'Cap imatge encara' : 'Sense imatge';
    row.appendChild(placeholder);

    if (showRightArrow) row.appendChild(this.buildIconButton('›', 'Mostra les imatges', () => this.cycle(1)));

    wrapper.appendChild(row);
  }

  private buildArrowSpacer(): HTMLElement {
    const spacer = document.createElement('div');
    spacer.className = 'w-8 h-8';
    spacer.setAttribute('aria-hidden', 'true');
    return spacer;
  }

  private renderAddView(wrapper: HTMLElement): void {
    const label = document.createElement('div');
    label.className = 'text-xs text-[#888] font-mono';
    label.textContent = 'Afegeix una imatge nova';
    wrapper.appendChild(label);

    const uploaderContainer = document.createElement('div');
    uploaderContainer.id = `${this.idPrefix}-add-uploader`;
    wrapper.appendChild(uploaderContainer);

    const uploader = new ImageUploader({
      container: uploaderContainer,
      idPrefix: `${this.idPrefix}-add`,
      hideLabel: true,
      onImageSelected: async (blob) => {
        const added = await this.options.uploadFn(blob);
        this.images.push(added);
        this.options.onPoolChanged?.(this.images);
        this.preAddView = null;
        this.view = { kind: 'image', index: this.images.length - 1 };
        this.render();
        this.fireSelectIfPick();
      },
    });
    uploader.render();

    const back = document.createElement('button');
    back.type = 'button';
    back.textContent = '‹ Torna';
    back.className = 'text-xs text-[var(--accent)] hover:text-[#e0e0e0] font-mono';
    back.addEventListener('click', (e) => {
      e.stopPropagation();
      this.exitAdd();
    });
    wrapper.appendChild(back);
  }

  private renderControlsRow(wrapper: HTMLElement): void {
    if (this.view.kind === 'add') return; // the add view has its own "back" control

    const row = document.createElement('div');
    row.className = 'flex gap-2';
    row.appendChild(this.buildIconButton('+', 'Afegeix una imatge nova', () => this.enterAdd()));
    wrapper.appendChild(row);
  }

  // ─── Actions ────────────────────────────────────────────────

  private cycle(delta: number): void {
    if (this.images.length === 0) return;
    const newIndex = this.view.kind === 'image'
      ? (this.view.index + delta + this.images.length) % this.images.length
      : (delta > 0 ? 0 : this.images.length - 1);
    this.view = { kind: 'image', index: newIndex };
    this.render();
    this.fireSelectIfPick();
  }

  private selectNone(): void {
    this.view = { kind: 'none' };
    this.render();
    this.options.onSelect?.(null);
  }

  private enterAdd(): void {
    this.preAddView = this.view;
    this.view = { kind: 'add' };
    this.render();
  }

  private exitAdd(): void {
    this.view = this.preAddView ?? { kind: 'none' };
    this.preAddView = null;
    this.render();
  }

  private fireSelectIfPick(): void {
    if (this.options.mode !== 'pick') return;
    if (this.view.kind === 'image') {
      this.options.onSelect?.(this.images[this.view.index]);
    }
  }

  // ─── Buttons ────────────────────────────────────────────────

  private buildIconButton(glyph: string, title: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = glyph;
    btn.title = title;
    btn.className =
      'w-8 h-8 flex items-center justify-center rounded-full border border-[var(--accent-alpha-40)] ' +
      'text-[var(--accent)] bg-transparent hover:bg-[var(--accent-alpha-13)] focus:outline-none ' +
      'focus:ring-2 focus:ring-[var(--accent)] font-mono text-lg';
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    return btn;
  }

  /**
   * The default-image indicator, itself the control for setting a new
   * default: highlighted and inert on the current default; grayed out but
   * clickable on any other image (clicking makes it the new default).
   */
  private buildStarButton(current: ImagePoolItem): HTMLButtonElement {
    const isDefault = !!current.isDefault;
    const badge = document.createElement('button');
    badge.type = 'button';
    badge.textContent = '★';
    badge.title = isDefault ? 'Imatge predeterminada' : 'Estableix com a predeterminada';
    badge.className =
      'absolute bottom-0 right-0 rounded-full w-6 h-6 flex items-center justify-center border text-sm transition-colors ' +
      (isDefault
        ? 'text-[var(--accent)] bg-[#0d1117] border-[var(--accent-alpha-40)] cursor-default'
        : 'text-[#555] bg-[#0d1117] border-[#333] hover:text-[var(--accent)] hover:border-[var(--accent-alpha-40)] cursor-pointer');

    if (isDefault) {
      badge.disabled = true;
    } else if (this.options.setDefaultFn) {
      badge.addEventListener('click', async (e) => {
        e.stopPropagation();
        await this.options.setDefaultFn!(current.id);
        this.images.forEach(img => { img.isDefault = img.id === current.id; });
        this.options.onPoolChanged?.(this.images);
        this.render();
      });
    }
    return badge;
  }

  /**
   * The corner × on the currently-displayed image: pick mode clears the
   * selection to "no image"; manage mode arm/confirm-deletes it. `null`
   * when manage mode has no `deleteFn` — nothing to show.
   */
  private buildCloseButton(current: ImagePoolItem): HTMLButtonElement | null {
    if (this.options.mode === 'pick') {
      return this.buildOverlayButton('×', 'Sense imatge', () => this.selectNone());
    }
    if (this.options.deleteFn) {
      return this.buildDeleteButton(current);
    }
    return null;
  }

  private buildOverlayButtonElement(glyph: string, title: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = glyph;
    btn.title = title;
    btn.className =
      'absolute -top-2 -right-2 rounded-full w-6 h-6 flex items-center justify-center border text-sm ' +
      'text-[var(--accent)] bg-[#0d1117] border-[var(--accent-alpha-40)] hover:bg-[var(--accent-alpha-13)] ' +
      'focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';
    return btn;
  }

  private buildOverlayButton(glyph: string, title: string, onClick: () => void): HTMLButtonElement {
    const btn = this.buildOverlayButtonElement(glyph, title);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    return btn;
  }

  /** Two-click arm/confirm delete of the currently-displayed image, as the corner ×. */
  private buildDeleteButton(current: ImagePoolItem): HTMLButtonElement {
    const btn = this.buildOverlayButtonElement('×', 'Esborra la imatge');
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (!this.deleteArmed) {
        this.deleteArmed = true;
        btn.classList.add('bg-[var(--accent)]', 'text-[#0d1117]');
        btn.title = 'Confirma l\'esborrat';
        this.outsideClickHandler = (ev: MouseEvent) => {
          if (ev.target !== btn) {
            this.deleteArmed = false;
            this.render();
          }
        };
        document.addEventListener('click', this.outsideClickHandler);
        return;
      }
      this.disarmDelete();
      await this.options.deleteFn!(current.id);
      this.images = this.images.filter(img => img.id !== current.id);
      this.options.onPoolChanged?.(this.images);
      this.render();
    });
    return btn;
  }

  private disarmDelete(): void {
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    this.deleteArmed = false;
  }

  /** Clean up any pending listeners (e.g. an armed delete) when the host closes. */
  destroy(): void {
    this.disarmDelete();
  }
}
